import * as ActionTypes from '../../../constants';

/**
 * Hide the toast.
 *
 * `generation` is an internal tag used by `showToast`'s auto-hide timer: the reducer ignores the
 * action when the tag is stale (i.e. a newer toast has since been shown). Callers dismissing the
 * toast on purpose — the close button, or clearing a busy toast — pass nothing and always win.
 */
export function hideToast(generation) {
  return {
    type: ActionTypes.HIDE_TOAST,
    generation
  };
}

/**
 * Temporary fix until #2206 is merged.
 * Supports legacy two-action syntax:
 *    dispatch(setToastText('Toast.SketchFailedSave'));
 *    dispatch(showToast(1500));
 * And also supports proposed single-action syntax with message and optional timeout.
 *    dispatch(showToast('Toast.SketchFailedSave'));
 *    dispatch(showToast('Toast.SketchSaved', 5500));
 *
 * Showing a toast also CLEARS any busy toast (`showBusyToast`) still on screen — that is how an
 * operation's completion message replaces its in-progress one.
 */
export function showToast(textOrTime, timeout = 1500) {
  return (dispatch, getState) => {
    let time = timeout;
    let text;
    if (typeof textOrTime === 'string') {
      text = textOrTime;
      // eslint-disable-next-line no-use-before-define
      dispatch(setToastText(textOrTime));
    } else {
      time = textOrTime;
    }
    dispatch({
      type: ActionTypes.SHOW_TOAST,
      text
    });
    // Tag the auto-hide with the generation this SHOW_TOAST just produced, so it can only ever hide
    // its OWN toast. Without this, a 1500 ms timer left over from a previous message hides whatever
    // happens to be on screen when it fires.
    // `|| {}` for stores without a toast slice (partial mock states in tests): generation is then
    // undefined, the hide is untagged, and the behaviour is exactly what it was before the guard.
    const { generation } = getState().toast || {};
    setTimeout(() => dispatch(hideToast(generation)), time);
  };
}

/**
 * Show a persistent "operation in progress" toast: the text plus an inline spinner, with NO
 * auto-hide. It stays up until the next `showToast` (the completion message) or an explicit
 * `hideToast()`.
 *
 * Prefer `runWithBusyToast` (../utils/busyToast) over calling this directly — it adds the grace
 * period that keeps fast operations silent, and guarantees the toast is cleared on both the success
 * and the failure path.
 */
export function showBusyToast(text) {
  return {
    type: ActionTypes.SHOW_TOAST,
    text,
    isBusy: true
  };
}

export function setToastText(text) {
  return {
    type: ActionTypes.SET_TOAST_TEXT,
    text
  };
}
